import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useCatalog';
import { EditorialImage } from './ui/EditorialImage';
import { useCurrency } from '../hooks/useCurrency';

export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const { data: products } = useProducts();
  const { format } = useCurrency();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!open) return null;

  const results =
    query.trim().length > 0
      ? (products ?? []).filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()))
      : [];

  return (
    <div className="fixed inset-0 z-[100]">
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
      />
      <div className="relative mx-auto mt-24 w-full max-w-2xl bg-cream p-6 shadow-2xl md:p-8">
        <div className="flex items-center gap-3 border-b border-stone-light pb-4">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="text"
            placeholder="Search prints, collections…"
            className="w-full bg-transparent text-lg text-ink placeholder:text-stone focus:outline-none"
          />
          <button type="button" onClick={onClose} className="text-sm text-stone hover:text-ink">
            Esc
          </button>
        </div>

        {query.trim().length > 0 && (
          <div className="mt-5 max-h-[60vh] overflow-y-auto">
            {results.length === 0 ? (
              <p className="py-8 text-center text-sm text-stone">No results for “{query}”.</p>
            ) : (
              <div className="space-y-1">
                {results.slice(0, 8).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-4 p-2 hover:bg-paper"
                  >
                    <EditorialImage
                      src={product.images?.[0]?.path}
                      seed={`product-${product.slug}`}
                      alt={product.name}
                      duotone={false}
                      size="thumb"
                      aspect="aspect-square"
                      className="w-14 shrink-0"
                    />
                    <div>
                      <p className="text-sm font-medium text-ink">{product.name}</p>
                      <p className="text-sm text-stone">{format(product.base_price)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
